import {Box, Tooltip, useTheme} from "@mui/material";
import {ACard, ADataGrid, ADataGridColumn, ADataGridFilter, PagedSearchParams} from "@atiautomacao/ati-ui-library";
import React, {useCallback, useEffect, useState} from "react";
import {extractFiltersFromColumns} from "../../../Utils/DataUitils";
import {
    DeleteTeleObjectSpecificationTypeReactQuery,
    TeleObjectSpecificationTypeReactQuery
} from "./TeleObjectSpecificationTypeReactQuery";
import {useNavigate} from "react-router-dom";
import {SystemRoutes} from "../../../Utils/RouteUtils";
import IconButton from "@mui/material/IconButton";
import {Delete, Edit} from "@mui/icons-material";
import ConfirmDialog from "../../../Shared/Components/ConfirmDialog";
import {QueryClient, QueryClientProvider} from "react-query";
import {useAppSelector} from "../../../Config/Hooks";
import {hasPermission} from "../../../Shared/Auth/AuthenticationUtil";
import {AUTHORITIES} from "../../../Config/Constants";
import {HeaderMenu} from "../HeaderMenu";

export const initColumns = [
    {
        name: 'id',
        label: 'ID',
        align: 'center',
        visibility: false,
        minWidth: 10,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        },
        filter: {name: 'id', condition: 'equals', value: '', sort: ''}
    },
    {
        name: 'name',
        label: 'Nome',
        align: 'left',
        minWidth: 10,
        visibility: true,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        },
        filter: {name: 'name', condition: 'contains', value: '', sort: 'asc'}
    },
] as ADataGridColumn[]

export type ActionConfig = {
    actionName: string;
    disabled: boolean;
};

export interface RowTeleObjectSpecificationType {
    id?: number;
    name: string;
    actionsConfig?: Array<ActionConfig>
}

export const TeleObjectSpecificationTypeSettingsPage = () =>{

    const account = useAppSelector(state => state.authentication.account);
    const isAuthorizedToSave = hasPermission(account?.authoritySet, [
        AUTHORITIES.SYSADMIN, AUTHORITIES.SAVE_SETTINGS, AUTHORITIES.SAVE_TELE_OBJECT_SPECIFICATION_TYPE
    ]);

    const isAuthorizedToDelete = hasPermission(account?.authoritySet, [
        AUTHORITIES.SYSADMIN, AUTHORITIES.SAVE_SETTINGS, AUTHORITIES.DELETE_TELE_OBJECT_SPECIFICATION_TYPE
    ]);

    const [pagedSearchParams, setPagedSearchParams] = useState(new PagedSearchParams(extractFiltersFromColumns(initColumns), 0, 100));
    const [totalOfRecords, setTotalOfRecords] = useState(0);
    const { data, isSuccess } = TeleObjectSpecificationTypeReactQuery(pagedSearchParams);
    const [rows, setRows] = useState<Array<RowTeleObjectSpecificationType>>([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedToDelete, setSelectedToDelete] = useState<any>(null);



    const navigate = useNavigate();
    const mutation = DeleteTeleObjectSpecificationTypeReactQuery(pagedSearchParams);

    const handleOnFiltersChange = useCallback((dataGridFilters: Array<ADataGridFilter>, page: number, pageSize: number) => {
        let newPageParams: PagedSearchParams = new PagedSearchParams(dataGridFilters, page, pageSize);
        setPagedSearchParams(
            newPageParams
        );
    }, []);


    useEffect(() => {
        if(isSuccess){
            setTotalOfRecords(data?.totalOfRecords);
            if(data?.data){
                let rowsTeleObjectSpecification = data.data.map((teleObjectSpecification: any) => {
                    return {
                        id: teleObjectSpecification.id,
                        name: teleObjectSpecification.name,
                        actionsConfig: [
                            {actionName: "edit", disabled: !isAuthorizedToSave},
                            {actionName: "delete", disabled: !isAuthorizedToDelete},
                        ]
                    }
                });

                setRows(rowsTeleObjectSpecification);
            }
        }
    }, [data?.data]);


    const handleEditTeleObjectSpecificationType = useCallback((data: any) => {
        navigate(SystemRoutes.SETTINGS_TELE_OBJECT_SPECIFICATION_TYPE_FORM, { state: { id: data.id } });
    },[navigate]);

    const handleDeleteTeleObjectSpecificationType = useCallback((data: any) => {
        setSelectedToDelete(data.id);
        setOpenDialog(true);
    },[]);

    const handleDialogConfirm = async () => {
        mutation.mutate({ id: selectedToDelete, pagedSearchParams });
        setSelectedToDelete(null);
        handleDialogToggle();
    }

    useEffect(() => {
        if(mutation.isSuccess) {
            setPagedSearchParams(new PagedSearchParams(extractFiltersFromColumns(initColumns), 0, 50))
        }

    }, [mutation.isSuccess]);

    const handleDialogToggle = () => {
        setOpenDialog(!openDialog);
    }

    const handleDialogClose = () => {
        handleDialogToggle();
    }

    const theme = useTheme();

    return(
        <>
            <HeaderMenu systemRoutes={SystemRoutes.SETTINGS_TELE_OBJECT_SPECIFICATION_TYPE_FORM} disableSave={!isAuthorizedToSave}/>
            <Box>
                <ACard ariaLabel={"settings-tele-object-specification-type-page"}>
                    <ADataGrid
                        ariaLabel={"settings-grid-tele-object-specification-type"}
                        columns={initColumns}
                        rows={rows}
                        page={pagedSearchParams.page}
                        rowsPerPage={pagedSearchParams.size}
                        loading={false}
                        totalOfRecords={totalOfRecords}
                        hideSelection={true}
                        onFiltersChange={handleOnFiltersChange}
                        actions={
                            <>
                                <IconButton name="edit" size={"small"} aria-label="Edit" disabled={!isAuthorizedToSave} onClick={(value) => handleEditTeleObjectSpecificationType(value)}>
                                    <Tooltip title="Editar">
                                        <Edit/>
                                    </Tooltip>
                                </IconButton>
                                <IconButton name="delete" size={"small"} aria-label="Delete" disabled={!isAuthorizedToDelete} onClick={(value) => handleDeleteTeleObjectSpecificationType(value)}>
                                    <Tooltip title="Excluir">
                                        <Delete />
                                    </Tooltip>
                                </IconButton>
                            </>
                        }
                    />
                </ACard>
                <ConfirmDialog
                    title={'Deletar'}
                    description={'Deseja deletar esse Tele Objeto Específico?'}
                    open={openDialog}
                    handleConfirm={handleDialogConfirm}
                    handleClose={handleDialogClose}
                />
            </Box>

        </>
    );
}

export default function TeleObjectSpecificationTypeHomePage(){
    const queryClient = new QueryClient();
    return (
        <QueryClientProvider client={queryClient}>
            <TeleObjectSpecificationTypeSettingsPage />
        </QueryClientProvider>
    )
}
